// Define the form validation schema using Zod

import { isDivisor } from "../../../../../components/DashBoard/GTT/GTTBody/gttHelper";
import { z } from "zod";

export const orderSchema = z
  .object({
    memorizedLTP: z.number(), // only for validation this is needed
    memerizedTick: z.number(), // only for validation this is needed
    isReducedMargin: z.boolean(),
    low: z.number(),
    high: z.number(),
    isIceBerg: z.boolean(),

    numberOfLegs: z.string().optional(),

    lots: z
      .string()
      .min(1, "Please Enter Order Qunatity")
      .refine(
        (val) => !isNaN(Number(val)) && Number(val) > 0,
        "Order Qunatity Must Be Greater Than 0"
      ),
    price: z.string(),
    triggerPrice: z.string(),
    orderType: z.enum(["Limit", "Market"]),
    isStopLoss: z.boolean(),
    validity: z.enum(["DAY", "IOC", "AMO", ""]),
    tradeDirection: z.enum(["Buy", "Sell"]),
    type: z.enum(["GTT-M", "GTT-L", "Iceberg", ""]),

    isGTTStopLoss: z.boolean(),
    gttStoplossValue: z.string(),

    isGTTTarget: z.boolean(),
    gttTargetValue: z.string(),
  })
  .superRefine((data, ctx) => {
    // Existing validations
    if (data.isStopLoss) {
      const triggerPrice = data.triggerPrice;
      if (
        triggerPrice === "" ||
        isNaN(Number(triggerPrice)) ||
        Number(triggerPrice) <= 0
      ) {
        ctx.addIssue({
          path: ["triggerPrice"],
          code: z.ZodIssueCode.custom,
          message: "Please Enter Trigger Price For Stop Loss Order",
        });
      }
    }

    if (data.memerizedTick && !isDivisor(+data.price, data.memerizedTick)) {
      ctx.addIssue({
        path: ["price"], // Path specifies the field that has the issue
        code: z.ZodIssueCode.custom,
        message: `Price must be divisible by ${data.memerizedTick}`,
      });
    }

    if (
      (data.orderType === "Limit" && +data.price && +data.price < data.low) ||
      +data.price > data.high
    ) {
      ctx.addIssue({
        path: ["price"], // Path specifies the field that has the issue
        code: z.ZodIssueCode.custom,
        message: `Price must be in range`,
      });
    }

    if (data.type === "GTT-L" || data.type === "GTT-M") {
      const {
        gttStoplossValue,
        gttTargetValue,
        isGTTTarget,
        isGTTStopLoss,
        tradeDirection,
      } = data;

      // Validate Stop Loss value based on trade direction
      if (isGTTStopLoss) {
        const stopLossNum = Number(gttStoplossValue);

        if (!gttStoplossValue) {
          ctx.addIssue({
            path: ["gttStoplossValue"],
            code: z.ZodIssueCode.custom,
            message: "Please Enter Stop Loss Value",
          });
        } else if (isNaN(stopLossNum)) {
          ctx.addIssue({
            path: ["gttStoplossValue"],
            code: z.ZodIssueCode.custom,
            message: "Stop Loss value must be a valid number",
          });
        } else if (tradeDirection === "Buy" && stopLossNum >= 0) {
          ctx.addIssue({
            path: ["gttStoplossValue"],
            code: z.ZodIssueCode.custom,
            message: "For Buy orders, Stop Loss value must be negative",
          });
        } else if (tradeDirection === "Sell" && stopLossNum <= 0) {
          ctx.addIssue({
            path: ["gttStoplossValue"],
            code: z.ZodIssueCode.custom,
            message: "For Sell orders, Stop Loss value must be positive",
          });
        }
      }

      // Validate Target value based on trade direction
      if (isGTTTarget) {
        const targetNum = Number(gttTargetValue);

        if (!gttTargetValue) {
          ctx.addIssue({
            path: ["gttTargetValue"],
            code: z.ZodIssueCode.custom,
            message: "Please Enter Target Value",
          });
        } else if (isNaN(targetNum)) {
          ctx.addIssue({
            path: ["gttTargetValue"],
            code: z.ZodIssueCode.custom,
            message: "Target value must be a valid number",
          });
        } else if (tradeDirection === "Buy" && targetNum <= 0) {
          ctx.addIssue({
            path: ["gttTargetValue"],
            code: z.ZodIssueCode.custom,
            message: "For Buy orders, Target value must be positive",
          });
        } else if (tradeDirection === "Sell" && targetNum >= 0) {
          ctx.addIssue({
            path: ["gttTargetValue"],
            code: z.ZodIssueCode.custom,
            message: "For Sell orders, Target value must be negative",
          });
        }
      }
    }

    // Existing price validation
    if (data.orderType === "Limit") {
      const price = data.price;
      if (price === "" || isNaN(Number(price)) || Number(price) <= 0) {
        ctx.addIssue({
          path: ["price"],
          code: z.ZodIssueCode.custom,
          message: "Please Enter Order Price For Limit Order",
        });
      }
    }

    // if we had type === GTT-M and GTT-L then not selected ten ani fo AMO should be visible
    if (data.validity === "") {
      const isGTTSelected = data.type !== "";
      const validityOptions = isGTTSelected ? "DAY, IOC" : "DAY, IOC, AMO";

      ctx.addIssue({
        path: ["validity"],
        code: z.ZodIssueCode.custom,
        message: `Please select one of: ${validityOptions}`,
      });
    }

    // Ensure AMO is not selected when GTT is selected
    if (data.validity === "AMO" && data.type !== "") {
      ctx.addIssue({
        path: ["validity"],
        code: z.ZodIssueCode.custom,
        message: "AMO option is not available when GTT is selected",
      });
    }

    if (data.type && data.type !== "Iceberg" && !data.isGTTTarget && !data.isGTTStopLoss) {
      ctx.addIssue({
        path: ["type"],
        code: z.ZodIssueCode.custom,
        message: "Please select either Stop Loss or Target, or both.",
      });
    }
    

    // If the trade direction as Buy
    const price = (Number(data.price) ? Number(data.price) : data.memorizedLTP);

    if (data.tradeDirection === "Buy") {
      if (data.isStopLoss && data.orderType=="Limit") {
        if (price < +data.triggerPrice) {
          ctx.addIssue({
            path: ["triggerPrice"],
            code: z.ZodIssueCode.custom,
            message: `Trigger Price must be less than price`,
          });
        }
      }
    }

    // If the trade direction as Buy
    if (data.tradeDirection === "Sell") {
      if (data.isStopLoss && data.orderType=="Limit") {
        if (price > +data.triggerPrice) {
          ctx.addIssue({
            path: ["triggerPrice"],
            code: z.ZodIssueCode.custom,
            message: `Trigger Price must be greater than price`,
          });
        }
      }
    }
    // if we had iceberg
    if (data.type === "Iceberg") {
      const legsNum = Number(data.numberOfLegs);

      if (!legsNum) {
        ctx.addIssue({
          path: ["numberOfLegs"],
          code: z.ZodIssueCode.custom,
          message: "Please Enter Number of Legs between 2 to 10",
        });
      }

      // It Must be positive
      if (legsNum < 2 || legsNum > 10) {
        ctx.addIssue({
          path: ["numberOfLegs"],
          code: z.ZodIssueCode.custom,
          message: "Entered Value must be between 2 to 10",
        });
      }
    }

    if (
      data.numberOfLegs &&
      data.type &&
      data.type === "Iceberg" &&
      Number(data.lots) < Number(data.numberOfLegs)
    ) {
      ctx.addIssue({
        path: ["lots"],
        code: z.ZodIssueCode.custom,
        message: "Entered Lots must be greater than or equal to Number of legs",
      });
    }
  });
